<template>
  <b-modal
    id="RemoveRoleDialog"
    ref="RemoveRoleDialog"
    title="ถอดบทบาท"
    centered
    no-close-on-backdrop
    @hide="onHide"
    @ok.prevent="submit"
  >
    <!-- What is about to happen -->
    <div v-if="targetUser" class="remove-warn mb-3">
      <i class="fas fa-exclamation-triangle warn-icon"></i>
      <div>
        <div>
          กำลังจะถอด
          <b-badge variant="info">{{ roleLabel }}</b-badge>
          ของ
          <strong>{{ targetUser.firstNameTh }} {{ targetUser.lastNameTh }}</strong>
        </div>
        <div class="small text-muted mt-1">
          <span v-if="branchLabel">ในสาขา <strong>{{ branchLabel }}</strong> · </span>
          <span v-else-if="scope === 'clinic'">ระดับคลินิก · </span>
          มีผลทันที — ผู้ใช้จะเสียสิทธิ์ที่เกี่ยวข้องกับบทบาทนี้
        </div>
      </div>
    </div>

    <!-- Consequences before clicking -->
    <div class="impact-block mb-3">
      <div class="text-muted small mb-2">ผลกระทบที่จะเกิดขึ้น</div>
      <div
        v-for="(eff, i) in removeEffects"
        :key="'lose-' + i"
        class="impact-row"
      >
        <span class="impact-badge lose">−</span>
        <span>{{ eff }}</span>
      </div>
      <div
        v-for="(rk, i) in remainingRolesInScope"
        :key="'keep-' + i"
        class="impact-row"
      >
        <span class="impact-badge keep">✓</span>
        <span>ยังคงเป็น <strong>{{ roleName(rk) }}</strong>{{ branchLabel ? ` @ ${branchLabel}` : "" }}</span>
      </div>
      <div v-if="hasOtherBranches" class="impact-row">
        <span class="impact-badge keep">✓</span>
        <span>บทบาทในสาขาอื่นไม่เปลี่ยนแปลง</span>
      </div>
    </div>

    <b-form-group
      label="เหตุผล (อย่างน้อย 10 ตัวอักษร — บันทึกถาวรในประวัติ ลบไม่ได้)"
      label-class="font-weight-bold"
      :invalid-feedback="reasonError"
      :state="reasonState"
    >
      <b-form-textarea
        v-model="reason"
        rows="3"
        :state="reasonState"
        placeholder="ระบุเหตุผลในการถอดบทบาท..."
      />
    </b-form-group>

    <b-alert v-if="errorMessage" show variant="danger" class="mt-2 mb-0">
      {{ errorMessage }}
    </b-alert>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" :disabled="isSubmitting" @click="cancel">
        ยกเลิก
      </b-button>
      <b-button
        variant="danger"
        :disabled="!canSubmit || isSubmitting"
        @click="ok"
      >
        <b-spinner v-if="isSubmitting" small class="mr-1" />
        ถอดบทบาท
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import axios from "axios";
import { ROLE_LABELS_TH, ROLE_REMOVE_EFFECTS_TH } from "@/constants/roles";

export default {
  name: "RemoveRoleDialog",
  data() {
    return {
      scope: "branch", // 'branch' or 'clinic'
      targetUser: null,
      roleKey: null,
      branchUrl: null,
      branchLabel: null,
      clinicUrl: null,
      reason: "",
      isSubmitting: false,
      errorMessage: "",
    };
  },
  computed: {
    roleLabel() {
      return ROLE_LABELS_TH[this.roleKey] || this.roleKey || "-";
    },
    // Consequences of losing this role (falls back to a generic line).
    removeEffects() {
      return (
        ROLE_REMOVE_EFFECTS_TH[this.roleKey] || [
          "ผู้ใช้จะเสียสิทธิ์ที่เกี่ยวข้องกับบทบาทนี้",
        ]
      );
    },
    // The branch row matching what we're removing from (branchUrl carries branchId).
    branchRow() {
      if (this.scope !== "branch" || !this.targetUser) return null;
      return (this.targetUser.branchRoles || []).find(
        b => String(b.branchId) === String(this.branchUrl)
      );
    },
    // Roles the user keeps in the SAME scope after this removal.
    remainingRolesInScope() {
      const keys = this.branchRow?.roleKeys || [];
      return keys.filter(k => k !== this.roleKey);
    },
    hasOtherBranches() {
      if (!this.targetUser) return false;
      return (this.targetUser.branchRoles || []).some(
        b => String(b.branchId) !== String(this.branchUrl)
      );
    },
    reasonState() {
      if (this.reason.length === 0) return null;
      return this.reason.trim().length >= 10;
    },
    reasonError() {
      if (this.reason.length > 0 && this.reason.trim().length < 10) {
        return "กรุณาระบุเหตุผลอย่างน้อย 10 ตัวอักษร";
      }
      return "";
    },
    canSubmit() {
      return this.roleKey && this.reason.trim().length >= 10;
    },
  },
  methods: {
    roleName(rk) {
      return ROLE_LABELS_TH[rk] || rk;
    },
    show(payload) {
      // payload: { scope, targetUser, roleKey, clinicUrl, branchUrl?, branchLabel? }
      this.scope = payload.scope || "branch";
      this.targetUser = payload.targetUser || null;
      this.roleKey = payload.roleKey || null;
      this.clinicUrl = payload.clinicUrl;
      this.branchUrl = payload.branchUrl || null;
      this.branchLabel = payload.branchLabel || null;
      this.reason = "";
      this.errorMessage = "";
      this.isSubmitting = false;
      this.$bvModal.show("RemoveRoleDialog");
    },
    close() {
      this.$bvModal.hide("RemoveRoleDialog");
    },
    onHide() {
      this.errorMessage = "";
      this.isSubmitting = false;
    },
    async submit() {
      if (!this.canSubmit || this.isSubmitting) return;
      this.errorMessage = "";
      this.isSubmitting = true;
      try {
        const url =
          this.scope === "clinic"
            ? `/api/userMgmt/${this.clinicUrl}/central-members/${this.targetUser.uid}/roles/${this.roleKey}`
            : `/api/userMgmt/${this.clinicUrl}/branch/${this.branchUrl}/members/${this.targetUser.uid}/roles/${this.roleKey}`;
        await axios.delete(url, {
          data: { reason: this.reason.trim() },
          skipGlobalErrorHandler: true,
        });
        this.$emit("removed", {
          scope: this.scope,
          uid: this.targetUser.uid,
          roleKey: this.roleKey,
        });
        this.close();
      } catch (err) {
        this.errorMessage =
          err.response?.data?.errorMessage?.message ||
          err.response?.data?.message ||
          err.message ||
          "ไม่สามารถถอดบทบาทได้";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.remove-warn {
  display: flex;
  gap: 0.625rem;
  align-items: flex-start;
  background: #fdecea;
  border: 1px solid #f3c0bb;
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.warn-icon {
  color: #c5392e;
  font-size: 1.25rem;
  margin-top: 0.125rem;
}

.impact-block {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
}

.impact-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.1875rem 0;
  font-size: 0.9rem;
}

.impact-badge {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border-radius: 0.3rem;
  display: grid;
  place-items: center;
  font-weight: 700;
  line-height: 1;
}

.impact-badge.lose {
  background: #f3c0bb;
  color: #8a241b;
}

.impact-badge.keep {
  background: #cfe2d4;
  color: #2f7a4d;
}
</style>
